// Phase 12.3 — RealtimeNotificationsProvider promoted from no-op shell to
// real producer.
//
// It now subscribes to:
//   - useIngestionHealth() (already polling /health/ingestion every 30s)
//     to detect platform-wide ingestion silence and per-device
//     transitions into stale/silent
//   - useLiveFeed() (already there) for live device/socket events
//
// On state changes it both raises a Sonner toast (transient, visible
// now) and pushes a row into useNotificationCenter (persistent, shown
// in the bell dropdown). The IDs are stable per-event so an open tab
// doesn't accumulate duplicates over a long outage.

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useIngestionHealth } from '@/hooks/useIngestionHealth';
import { useNotificationCenter } from '@/hooks/useNotificationCenter';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';
import { formatAge } from '@/types/ingestion';

export function RealtimeNotificationsProvider({ children }: { children: React.ReactNode }) {
  // Existing live-feed subscription — kept so child consumers
  // (useNotificationCenter etc.) continue to work.
  useRealtimeNotifications();

  const { data, isError } = useIngestionHealth();
  const { push } = useNotificationCenter();

  // Remember the prior status per-device so we only fire a transition
  // notification once (not on every 30s poll). A small ref-backed Map
  // keeps this out of the render path.
  const lastBucketRef = useRef<Map<string, string>>(new Map());
  const lastOverallRef = useRef<string | null>(null);
  const lastNetworkErrorAtRef = useRef<number>(0);

  useEffect(() => {
    if (isError) {
      // Network-level outage to /health/ingestion itself — usually means
      // the API is down. Throttle to once per 5 minutes.
      const now = Date.now();
      if (now - lastNetworkErrorAtRef.current > 5 * 60 * 1000) {
        lastNetworkErrorAtRef.current = now;
        toast.error('Operations API unreachable', {
          description: 'Cannot reach /health/ingestion. Telemetry status is unknown.',
        });
        push({
          id: `ops-unreachable-${Math.floor(now / 300000)}`,
          title: 'Operations API unreachable',
          description: 'Cannot reach /health/ingestion. Telemetry status is unknown.',
          icon: 'server-off',
        });
      }
      return;
    }
    // Phase 19 — a partial /health/ingestion payload (e.g. test mocks,
    // a misrouted nginx, an upstream proxy stub) used to crash at
    // `data.devices.rows` and tear down the whole app. Bail out unless
    // the payload has the required shape; the UI will catch up on the
    // next poll once the upstream is healthy.
    if (!data || !data.devices || !data.screenshots) return;

    // Overall ingestion transitions — only notify when crossing into
    // a worse state (fresh → slow/stale/silent). Recovery into fresh
    // also gets a toast so the watcher sees the all-clear.
    const overall = data.status;
    const prevOverall = lastOverallRef.current;
    if (prevOverall && prevOverall !== overall) {
      if ((prevOverall === 'fresh' || prevOverall === 'slow') && (overall === 'stale' || overall === 'silent')) {
        const age = formatAge(data.screenshots.ageMs);
        toast.error('Ingestion has gone silent', {
          description: `No telemetry uploads for ${age}. Check agent connectivity.`,
        });
        push({
          id: `ingestion-silent-${data.screenshots.lastAt || age}`,
          title: 'Ingestion silent',
          description: `No telemetry uploads for ${age}.`,
          icon: 'alert-triangle',
        });
      } else if ((prevOverall === 'stale' || prevOverall === 'silent') && overall === 'fresh') {
        toast.success('Ingestion recovered', {
          description: 'Agents are reporting again.',
        });
        push({
          id: `ingestion-recovered-${Date.now()}`,
          title: 'Ingestion recovered',
          description: 'Agents are reporting again.',
          icon: 'check-circle',
        });
      }
    }
    lastOverallRef.current = overall;

    // Per-device transitions into stale/silent — surface the hostname
    // so an admin knows which box to look at.
    const last = lastBucketRef.current;
    (data.devices.rows || []).forEach((row) => {
      const prev = last.get(row.deviceId);
      if (prev !== row.ageBucket) {
        if (prev && (prev === 'fresh' || prev === 'slow') && (row.ageBucket === 'stale' || row.ageBucket === 'silent')) {
          const label = row.hostname || row.deviceId.slice(0, 8);
          toast.warning(`Agent silent: ${label}`, {
            description: `Last contact ${formatAge(row.ageMs)} ago.`,
          });
          push({
            id: `agent-silent-${row.deviceId}-${row.ageBucket}`,
            title: `Agent silent: ${label}`,
            description: `Last contact ${formatAge(row.ageMs)} ago.`,
            icon: 'wifi-off',
          });
        } else if (prev && (prev === 'stale' || prev === 'silent') && row.ageBucket === 'fresh') {
          const label = row.hostname || row.deviceId.slice(0, 8);
          toast.success(`Agent reconnected: ${label}`);
          push({
            id: `agent-reconnected-${row.deviceId}-${Date.now()}`,
            title: `Agent reconnected: ${label}`,
            description: 'Heartbeat resumed.',
            icon: 'wifi',
          });
        }
        last.set(row.deviceId, row.ageBucket);
      }
    });
  }, [data, isError, push]);

  return <>{children}</>;
}
